import { MAuswahl, MAuswahlwert } from "../../metamodell";
import * as _ from "lodash";
import { Segment } from "../arrangement/Segment";
import { findAuswahl, partToWorkflow } from "../arrangement/elementFunctions";
import { v3 } from "oc-threejs/short3";
import React from "react";

/**
 * Aus gegebenem Array von Auswahlwerten wähle in folgender Priorität:
 * 1. die Voreinstellung
 * 2. die niedrigste Sortierung
 * 3. den ersten Wert
 *
 * @param auswahlwerte
 */
export function defaultOf(auswahlwerte: MAuswahlwert[]) {
  return (
    auswahlwerte.find((auswahlwert) => auswahlwert.Voreinstellung) ||
    _.minBy(auswahlwerte, "Sortierung") ||
    _.first(auswahlwerte)
  );
}

/**
 * Holt die passenden Informationen aus der Tabelle Auswahl.
 * Abhängig vom aktuellen Workflow und der DetailSerie.
 * Nutzt jetzt die Funktion aus elementFunctions bzw. die schon bestimmten Werte der AuswahlAccessoren
 * @param auswahlKey
 */
export function getAuswahlData(auswahlKey: string): MAuswahl {
  let auswahlData = APP.arrangement.auswahlAccessoren[auswahlKey]?.auswahl;
  if (auswahlData) return auswahlData;

  const { DetailSerien, Workflow } = partToWorkflow(APP.arrangement.getCurrentElement());
  auswahlData = findAuswahl(auswahlKey, DetailSerien, Workflow);
  return auswahlData;
}

/**
 * Gibt minimale und maximale x-Koordinate der Konfiguration an
 * @param segment
 */
export function getMinMaxXForSegment(segment: Segment): { minx: number; maxx: number } {
  const everything = segment.getPartsAndPfosten().filter((x) => !x.isEmpty);
  if (everything.length == 0) return { minx: 0, maxx: 0 };

  return { minx: segment.getX(_.first(everything), 0), maxx: segment.getX(_.last(everything), 1) };
}

/**
 * Gibt das Zentrum (x-Achse) des Segments an
 * @param segment
 */
export function getWorldCenterOfSegmentParts(segment: Segment): number {
  const { minx, maxx } = getMinMaxXForSegment(segment);
  const minxWorld = segment.getWorldPositionOfX(minx).x;
  const maxxWorld = segment.getWorldPositionOfX(maxx).x;
  return minxWorld + (maxxWorld - minxWorld) / 2;
}

/**
 * Gibt vier Positionen in Welt-Koordinaten aus, die die äußersten Eckpunkte der Szene darstellen sollen
 * Damit kann z.B. geprüft werden ob noch etwas sichtbar ist.
 * Reihenfolge: links oben, links unten, rechts oben, rechts unten
 * @param segment
 */
export function getWorldPositionsOfEdges(segment: Segment): number[] {
  const everything = segment.getPartsAndPfosten().filter((x) => !x.isEmpty);
  const firstPart = _.first(everything);
  const lastPart = _.last(everything);
  const äusserePositionen = [
    firstPart
      .getWorldPosition()
      .clone()
      .add(v3(0, firstPart.size.y, 0)),
    firstPart.getWorldPosition().clone(),
    lastPart
      .getWorldPosition()
      .clone()
      .add(v3(0, lastPart.size.y, 0)),
    lastPart.getWorldPosition().clone(),
  ];
  return äusserePositionen;
}

export function EditInZaunButton({ code = APP.configuration.Code }: { code?: string }) {
  const zaunUrl = APP.settings.zaunConfiguratorUrl;
  const finalUrl = code == "" ? zaunUrl : zaunUrl + "/?Code=" + code;
  return (
    <a
      href={finalUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="clickable outlined-button fertig-button font-size-1"
      aria-label={I18N("Im Zaunplaner bearbeiten (öffnet in neuem Tab)")}
    >
      <i className="icon icon-pen-to-square-solid" aria-hidden="true"></i>
      <span>{I18N("edit in configurator")}</span>
    </a>
  );
}

/**
 * Leitet weiter zur Zaunplaner-Url
 * @param code gegebener KonfigurationsCode (oder App.configuration.Code)
 */
export function redirectToZaunplaner(code = APP.configuration.Code) {
  console.log("Weiterleitung zu Zaunplaner mit Code: " + code);
  const url = APP.settings.zaunConfiguratorUrl;
  document.location.href = code == "" ? url : url + "/?Code=" + code;
}
